import { create } from 'zustand';
import { ProjectInfoData } from '../dto/EntityRepository';
import { ProjectInfoType } from '../enum/EnumRepository';
import { isSameProjectInfo, normalizeProjectInfoType, withoutVenueIds } from '../util/projectInfo';
import type { ResourceScope, ResourceStatus } from '../util/scopedResource';

interface ProjectInfoListState {
  projectInfoList: ProjectInfoData[];
  listOwnerAui: string | null;
  listScope: ResourceScope | null;
  listProjectId: string | null;
  status: ResourceStatus;
  // 기본 projectInfoList에 변경된 객체를 확인할 수 있는 hasChanged 속성을 추가
  setProjectInfoList: (piList: ProjectInfoData[]) => void;
  beginProjectInfoListFetch: (ownerAui: string, scope: ResourceScope, projectId: string) => void;
  setProjectInfoListWithScope: (
    piList: ProjectInfoData[],
    ownerAui: string,
    scope: ResourceScope,
    projectId: string,
  ) => void;
  setProjectInfoListError: () => void;
  // 기본 projectInfoList에 hasChanged 속성을 true로 변경하여 변경됨을 체크한다.
  updateProjectInfo: (id: string, updates: Partial<ProjectInfoData>) => void;

  // 새로운 projectInfo를 추가
  afterCreateProjectInfo: (projectInfo: ProjectInfoData) => void;
  afterUpdateProjectInfoBulk: (updatedProjectInfoList: ProjectInfoData[]) => void;
  // 왜 여기 있나요? 
  // 삭제된 object의 id를 알 수 없기 때문에
  // delete 성공처리를 하는 hook에서 처리하지 못한다.
  afterDeleteProjectInfo: (id: string) => void;
  clearProjectInfoList: () => void;
}

const isSameProjectInfoList = (prev: ProjectInfoData[], next: ProjectInfoData[]): boolean =>
  prev.length === next.length &&
  prev.every((info, index) => isSameProjectInfo(info, next[index]));

export const useProjectInfoListStore = create<ProjectInfoListState>((set) => ({
  projectInfoList: [],
  listOwnerAui: null,
  listScope: null,
  listProjectId: null,
  status: 'idle',
  setProjectInfoList: (projectInfoList) =>
    set(() => ({
      projectInfoList: projectInfoList.map((pi) => ({ ...pi, hasChanged: false })),
    })),
  beginProjectInfoListFetch: (ownerAui, scope, projectId) => set((state) => {
    const normalizedId = String(projectId);
    // 같은 owner+scope+projectId 재fetch: success 유지(SWR). 다르면 loading으로 숨김.
    if (
      state.listOwnerAui === ownerAui &&
      state.listScope === scope &&
      state.listProjectId === normalizedId &&
      state.status === 'success'
    ) {
      return state;
    }
    return { status: 'loading' };
  }),
  setProjectInfoListWithScope: (projectInfoList, ownerAui, scope, projectId) =>
    set((state) => {
      const normalizedId = String(projectId);
      const normalized = projectInfoList.map((pi) => ({ ...pi, hasChanged: false }));
      if (
        state.status === 'success' &&
        state.listOwnerAui === ownerAui &&
        state.listScope === scope &&
        state.listProjectId === normalizedId &&
        isSameProjectInfoList(state.projectInfoList, normalized)
      ) {
        return state;
      }
      return {
        projectInfoList: normalized,
        listOwnerAui: ownerAui,
        listScope: scope,
        listProjectId: normalizedId,
        status: 'success',
      };
    }),
  setProjectInfoListError: () => set({ status: 'error' }),
  updateProjectInfo: (id, updates) =>
    set(({ projectInfoList }) => ({
      projectInfoList: projectInfoList.map((pi) => {
        if (pi.id !== id) return pi;
        const nextType = normalizeProjectInfoType(updates.type ?? pi.type);
        const nextValues = updates.customValues ?? pi.customValues;
        return {
          ...pi,
          ...updates,
          type: nextType,
          customValues: nextType === ProjectInfoType.LOCATION
            ? nextValues
            : withoutVenueIds(nextValues),
          hasChanged: true,
        };
      }),
    })),
  afterCreateProjectInfo: (projectInfo) =>
    set(({ projectInfoList }) => ({
      projectInfoList: [...projectInfoList,     // 기존 projectInfoList
      { ...projectInfo, hasChanged: false }   // 새롭게 생성한 projectInfo
      ]
    })),
  afterDeleteProjectInfo: (id) =>
    set(({ projectInfoList }) => ({
      projectInfoList: projectInfoList.filter((pi) => pi.id !== id)
    })),
  afterUpdateProjectInfoBulk: (updatedProjectInfoList) =>
    set(({ projectInfoList }) => {
      if (updatedProjectInfoList.length === 0) return { projectInfoList };
      const updatedById = new Map(updatedProjectInfoList.map((pi) => [pi.id, pi]));
      return {
        projectInfoList: projectInfoList.map((pi) => {
          const updated = updatedById.get(pi.id);
          return updated ? { ...updated, hasChanged: false } : pi;
        }),
      };
    }),
  clearProjectInfoList: () => set((state) => {
    if (
      state.projectInfoList.length === 0 &&
      state.listOwnerAui === null &&
      state.listScope === null &&
      state.listProjectId === null &&
      state.status === 'idle'
    ) {
      return state;
    }
    return {
      projectInfoList: [],
      listOwnerAui: null,
      listScope: null,
      listProjectId: null,
      status: 'idle',
    };
  }),
}));
